import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Package, Plus, Edit2, Trash2, ArrowDownToLine, ArrowUpFromLine, BarChart3, Settings, AlertTriangle, CheckCircle, ChevronDown, Search, TriangleAlert, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ColorStorePage() {
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
  });
  const isAdmin = user.role === "admin" || user.username?.toLowerCase() === "divyang";
  const isDyeing = user.department?.toLowerCase() === "dyeing";
  const canWrite = !isDyeing && (isAdmin || user.department?.toLowerCase() === "color_store" || user.department?.toLowerCase() === "accounts");
  const canRecon = isAdmin || user.department?.toLowerCase() === "accounts";

  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [inwardEntries, setInwardEntries] = useState([]);
  const [outwardEntries, setOutwardEntries] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [settings, setSettings] = useState({ tolerance_pct: 3.0 });
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showInwardDialog, setShowInwardDialog] = useState(false);
  const [showOutwardDialog, setShowOutwardDialog] = useState(false);
  const [showToleranceDialog, setShowToleranceDialog] = useState(false);
  const [editingOutward, setEditingOutward] = useState(null);
  const [editingInward, setEditingInward] = useState(null);

  // Form states
  const [productForm, setProductForm] = useState({ name: "", category: "Chemical", unit: "Kg", standard_usage_pct: "", min_stock_threshold: "" });
  const [inwardForm, setInwardForm] = useState({ product_id: "", date: new Date().toISOString().split("T")[0], quantity: "", supplier_name: "", rate: "", entry_unit: "" });
  const [outwardForm, setOutwardForm] = useState({ product_id: "", date: new Date().toISOString().split("T")[0], lot_no: "", lot_kgs: "", usage_pct: "", qty_used: "", entry_unit: "" });
  const [toleranceForm, setToleranceForm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [inwardProductSearch, setInwardProductSearch] = useState("");
  const [outwardProductSearch, setOutwardProductSearch] = useState("");
  const [lotNoFilter, setLotNoFilter] = useState("");

  // Reconciliation
  const [reconStartDate, setReconStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [reconEndDate, setReconEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [reconData, setReconData] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/color-store/products`, { headers });
      setProducts(res.data);
    } catch (err) { /* ignore */ }
  }, []);

  const fetchInward = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/color-store/inward`, { headers });
      setInwardEntries(res.data);
    } catch (err) { /* ignore */ }
  }, []);

  const fetchOutward = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/color-store/outward`, { headers });
      setOutwardEntries(res.data);
    } catch (err) { /* ignore */ }
  }, []);

  const fetchStock = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/color-store/stock`, { headers });
      setStockItems(res.data);
    } catch (err) { /* ignore */ }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/color-store/settings`, { headers });
      setSettings(res.data);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === "inward") fetchInward();
    if (activeTab === "outward") fetchOutward();
    if (activeTab === "stock") { fetchStock(); fetchProducts(); }
  }, [activeTab]);

  // ─── Product Handlers ─────────────────────────────────────
  const handleSaveProduct = async () => {
    try {
      if (editingProduct) {
        const payload = {};
        if (productForm.name !== editingProduct.name) payload.name = productForm.name;
        if (productForm.category !== editingProduct.category) payload.category = productForm.category;
        if (productForm.unit !== editingProduct.unit) payload.unit = productForm.unit;
        if (isAdmin && parseFloat(productForm.standard_usage_pct) !== editingProduct.standard_usage_pct) {
          payload.standard_usage_pct = parseFloat(productForm.standard_usage_pct);
        }
        if (isAdmin && parseFloat(productForm.min_stock_threshold) !== (editingProduct.min_stock_threshold || 0)) {
          payload.min_stock_threshold = parseFloat(productForm.min_stock_threshold);
        }
        await axios.put(`${API}/api/color-store/products/${editingProduct.id}`, payload, { headers });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/api/color-store/products`, {
          ...productForm,
          standard_usage_pct: parseFloat(productForm.standard_usage_pct),
          min_stock_threshold: parseFloat(productForm.min_stock_threshold) || 0
        }, { headers });
        toast.success("Product created");
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      setProductForm({ name: "", category: "Chemical", unit: "Kg", standard_usage_pct: "", min_stock_threshold: "" });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/api/color-store/products/${id}`, { headers });
      toast.success("Product deleted");
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  // ─── Unit Conversion Helper ─────────────────────────────
  const convertToMasterUnit = (value, fromUnit, masterUnit) => {
    if (!value || fromUnit === masterUnit) return parseFloat(value);
    if (fromUnit === 'Gram' && masterUnit === 'Kg') return parseFloat(value) / 1000;
    if (fromUnit === 'Kg' && masterUnit === 'Gram') return parseFloat(value) * 1000;
    return parseFloat(value);
  };

  const getUnitOptions = (masterUnit) => {
    if (masterUnit === 'Kg' || masterUnit === 'Gram') return ['Kg', 'Gram'];
    return [masterUnit];
  };

  // ─── Inward Handler ───────────────────────────────────────
  const handleSaveInward = async () => {
    try {
      const selectedProduct = products.find(p => p.id === inwardForm.product_id);
      const convertedQty = convertToMasterUnit(inwardForm.quantity, inwardForm.entry_unit, selectedProduct?.unit);
      const payload = {
        ...inwardForm,
        quantity: convertedQty,
        rate: inwardForm.rate ? parseFloat(inwardForm.rate) : null
      };
      if (editingInward) {
        await axios.put(`${API}/api/color-store/inward/${editingInward.id}`, payload, { headers });
        toast.success("Inward entry updated");
        setEditingInward(null);
        setShowInwardDialog(false);
        setInwardForm({ product_id: "", date: new Date().toISOString().split("T")[0], quantity: "", supplier_name: "", rate: "", entry_unit: "" });
        setInwardProductSearch("");
      } else {
        await axios.post(`${API}/api/color-store/inward`, payload, { headers });
        toast.success(`Inward entry recorded for ${selectedProduct?.name || 'product'}`);
        setInwardForm({ ...inwardForm, product_id: "", quantity: "", rate: "", entry_unit: "" });
        setInwardProductSearch("");
      }
      fetchInward();
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEditInward = (entry) => {
    setEditingInward(entry);
    setInwardForm({
      product_id: entry.product_id,
      date: entry.date,
      quantity: String(entry.quantity),
      supplier_name: entry.supplier_name || "",
      rate: entry.rate ? String(entry.rate) : "",
      entry_unit: entry.product_unit || ""
    });
    setInwardProductSearch(`${entry.product_name} (${entry.product_category})`);
    setShowInwardDialog(true);
  };

  const handleDeleteInward = async (id) => {
    if (!window.confirm("Delete this inward entry? Stock will be reversed.")) return;
    try {
      await axios.delete(`${API}/api/color-store/inward/${id}`, { headers });
      toast.success("Inward entry deleted");
      fetchInward();
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  // ─── Outward Handler ──────────────────────────────────────
  const handleSaveOutward = async () => {
    try {
      const selectedProduct = products.find(p => p.id === outwardForm.product_id);
      const convertedQty = convertToMasterUnit(outwardForm.qty_used, outwardForm.entry_unit, selectedProduct?.unit);
      const payload = {
        product_id: outwardForm.product_id,
        date: outwardForm.date,
        lot_no: outwardForm.lot_no,
        lot_kgs: parseFloat(outwardForm.lot_kgs),
        qty_used: convertedQty
      };
      if (isAdmin && outwardForm.usage_pct) {
        payload.usage_pct = parseFloat(outwardForm.usage_pct);
      }
      if (editingOutward) {
        await axios.put(`${API}/api/color-store/outward/${editingOutward.id}`, payload, { headers });
        toast.success("Outward entry updated");
        setEditingOutward(null);
        setShowOutwardDialog(false);
        setOutwardForm({ product_id: "", date: new Date().toISOString().split("T")[0], lot_no: "", lot_kgs: "", usage_pct: "", qty_used: "", entry_unit: "" });
        setOutwardProductSearch("");
      } else {
        const res = await axios.post(`${API}/api/color-store/outward`, payload, { headers });
        if (res.data.alert) {
          toast.warning(res.data.alert);
        } else {
          toast.success(`Outward entry recorded for ${selectedProduct?.name || 'product'}`);
        }
        // Reset product-specific fields but keep dialog open and retain date/lot_no/lot_kgs
        setOutwardForm({ ...outwardForm, product_id: "", usage_pct: "", qty_used: "", entry_unit: "" });
        setOutwardProductSearch("");
      }
      fetchOutward();
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEditOutward = (entry) => {
    setEditingOutward(entry);
    const prod = products.find(p => p.id === entry.product_id);
    setOutwardForm({
      product_id: entry.product_id,
      date: entry.date,
      lot_no: entry.lot_no,
      lot_kgs: String(entry.lot_kgs),
      usage_pct: String(entry.usage_pct),
      qty_used: String(entry.qty_used),
      entry_unit: prod?.unit || ""
    });
    setOutwardProductSearch(entry.product_name || "");
    setShowOutwardDialog(true);
  };

  const handleDeleteOutward = async (id) => {
    if (!window.confirm("Delete this outward entry? Stock will be restored.")) return;
    try {
      await axios.delete(`${API}/api/color-store/outward/${id}`, { headers });
      toast.success("Outward entry deleted");
      fetchOutward();
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  // ─── Reconciliation ───────────────────────────────────────
  const fetchRecon = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/color-store/reconciliation`, {
        headers, params: { start_date: reconStartDate, end_date: reconEndDate }
      });
      setReconData(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load reconciliation"); }
    setLoading(false);
  };

  // Tolerance
  const handleSaveTolerance = async () => {
    try {
      await axios.put(`${API}/api/color-store/settings/tolerance`, { tolerance_pct: parseFloat(toleranceForm) }, { headers });
      toast.success("Tolerance updated");
      setShowToleranceDialog(false);
      fetchSettings();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Auto-fetch usage_pct when product selected in outward form
  const selectedOutwardProduct = products.find(p => p.id === outwardForm.product_id);
  const expectedConsumption = selectedOutwardProduct && outwardForm.lot_kgs
    ? (parseFloat(outwardForm.lot_kgs) * (parseFloat(outwardForm.usage_pct || selectedOutwardProduct.standard_usage_pct) / 100)).toFixed(3)
    : null;

  const filteredProducts = products.filter(p =>
    !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="color-store-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Color Store</h1>
          <p className="text-muted-foreground text-sm mt-1">Chemicals & Dyes inventory management</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => { setToleranceForm(String(settings.tolerance_pct)); setShowToleranceDialog(true); }} data-testid="tolerance-btn">
              <Settings className="w-4 h-4 mr-1" /> Tolerance: ±{settings.tolerance_pct}%
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full max-w-2xl ${canRecon ? 'grid-cols-5' : 'grid-cols-4'}`}>
          <TabsTrigger value="products"><Package className="w-4 h-4 mr-1 hidden sm:inline" />Products</TabsTrigger>
          <TabsTrigger value="inward"><ArrowDownToLine className="w-4 h-4 mr-1 hidden sm:inline" />Inward</TabsTrigger>
          <TabsTrigger value="outward"><ArrowUpFromLine className="w-4 h-4 mr-1 hidden sm:inline" />Outward</TabsTrigger>
          <TabsTrigger value="stock"><Package className="w-4 h-4 mr-1 hidden sm:inline" />Stock</TabsTrigger>
          {canRecon && <TabsTrigger value="reconciliation" data-testid="recon-tab"><BarChart3 className="w-4 h-4 mr-1 hidden sm:inline" />Recon</TabsTrigger>}
        </TabsList>

        {/* ─── Products Tab ─── */}
        <TabsContent value="products" className="space-y-4">
          <div className="flex justify-between items-center gap-3">
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
            {canWrite && (
              <Button onClick={() => { setEditingProduct(null); setProductForm({ name: "", category: "Chemical", unit: "Kg", standard_usage_pct: "", min_stock_threshold: "" }); setShowProductDialog(true); }} data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Product
              </Button>
            )}
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Product Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Std Usage %</TableHead>
                    <TableHead className="text-right">Min Stock</TableHead>
                    <TableHead className="text-right">Current Stock</TableHead>
                    {canWrite && <TableHead className="text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map(p => (
                    <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell><Badge variant={p.category === "Dye" ? "default" : "secondary"}>{p.category}</Badge></TableCell>
                      <TableCell>{p.unit}</TableCell>
                      <TableCell className="text-right font-mono">{p.standard_usage_pct}%</TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">{(p.min_stock_threshold || 0).toFixed(2)}</TableCell>
                      <TableCell className={`text-right font-mono font-bold ${p.current_stock <= 0 ? 'text-red-600' : (p.min_stock_threshold > 0 && p.current_stock < p.min_stock_threshold) ? 'text-amber-600' : ''}`}>{p.current_stock.toFixed(2)}</TableCell>
                      {canWrite && (
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingProduct(p);
                              setProductForm({ name: p.name, category: p.category, unit: p.unit, standard_usage_pct: String(p.standard_usage_pct), min_stock_threshold: String(p.min_stock_threshold || 0) });
                              setShowProductDialog(true);
                            }}><Edit2 className="w-4 h-4" /></Button>
                            {isAdmin && <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {filteredProducts.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No products found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Inward Tab ─── */}
        <TabsContent value="inward" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Inward Entries (Stock In)</h3>
            {canWrite && (
              <Button onClick={() => { setEditingInward(null); setInwardForm({ product_id: "", date: new Date().toISOString().split("T")[0], quantity: "", supplier_name: "", rate: "", entry_unit: "" }); setInwardProductSearch(""); setShowInwardDialog(true); }} data-testid="add-inward-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Inward
              </Button>
            )}
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Date</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Stock After</TableHead>
                    <TableHead>By</TableHead>
                    {canWrite && <TableHead className="text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inwardEntries.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm">{e.date}</TableCell>
                      <TableCell className="font-medium">{e.product_name}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs">{e.product_category}</Badge></TableCell>
                      <TableCell className="text-right font-mono font-bold text-green-700">+{e.quantity} {e.product_unit}</TableCell>
                      <TableCell className="text-sm">{e.supplier_name || "-"}</TableCell>
                      <TableCell className="text-right text-sm">{e.rate ? `₹${e.rate}` : "-"}</TableCell>
                      <TableCell className="text-right font-mono">{e.stock_after?.toFixed(2)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.created_by}</TableCell>
                      {canWrite && (
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditInward(e)}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteInward(e.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {inwardEntries.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No inward entries yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Outward Tab ─── */}
        <TabsContent value="outward" className="space-y-4">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold">Outward Entries (Consumption)</h3>
              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Filter by Lot No..." value={lotNoFilter} onChange={e => setLotNoFilter(e.target.value)} className="pl-9 h-8 text-sm w-48" data-testid="lot-no-filter" />
              </div>
            </div>
            {canWrite && (
              <Button onClick={() => { setEditingOutward(null); setOutwardForm({ product_id: "", date: new Date().toISOString().split("T")[0], lot_no: "", lot_kgs: "", usage_pct: "", qty_used: "", entry_unit: "" }); setOutwardProductSearch(""); setShowOutwardDialog(true); }} data-testid="add-outward-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Outward
              </Button>
            )}
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 text-xs">
                    <TableHead>Date</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Lot No</TableHead>
                    <TableHead className="text-right">Lot Kgs</TableHead>
                    <TableHead className="text-right">Usage %</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead className="text-right">Actual Used</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Stock After</TableHead>
                    {canWrite && <TableHead className="text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outwardEntries.filter(e => !lotNoFilter || e.lot_no?.toLowerCase().includes(lotNoFilter.toLowerCase())).map(e => (
                    <TableRow key={e.id} className={e.status === 'over_consumption' ? 'bg-red-50/50' : e.status === 'under_consumption' ? 'bg-amber-50/50' : ''}>
                      <TableCell className="text-sm">{e.date}</TableCell>
                      <TableCell className="font-medium text-sm">{e.product_name}</TableCell>
                      <TableCell className="text-sm">{e.lot_no}</TableCell>
                      <TableCell className="text-right font-mono">{e.lot_kgs}</TableCell>
                      <TableCell className="text-right font-mono">{e.usage_pct}%</TableCell>
                      <TableCell className="text-right font-mono text-blue-700">{e.expected_consumption}</TableCell>
                      <TableCell className="text-right font-mono font-bold">{e.qty_used}</TableCell>
                      <TableCell className={`text-right font-mono font-bold ${e.variance > 0 ? 'text-red-600' : e.variance < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {e.variance > 0 ? '+' : ''}{e.variance} ({e.variance_pct}%)
                      </TableCell>
                      <TableCell className="text-center">
                        {e.status === 'within_control' && <Badge className="bg-green-100 text-green-800 text-xs">OK</Badge>}
                        {e.status === 'over_consumption' && <Badge className="bg-red-100 text-red-800 text-xs">Over</Badge>}
                        {e.status === 'under_consumption' && <Badge className="bg-amber-100 text-amber-800 text-xs">Under</Badge>}
                      </TableCell>
                      <TableCell className="text-right font-mono">{e.stock_after?.toFixed(2)}</TableCell>
                      {canWrite && (
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditOutward(e)} data-testid={`edit-outward-${e.id}`}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteOutward(e.id)} data-testid={`delete-outward-${e.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {outwardEntries.length === 0 && (
                    <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No outward entries yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Stock Tab ─── */}
        <TabsContent value="stock" className="space-y-4">
          <h3 className="font-semibold">SKU-wise Balance Stock</h3>
          {/* Low Stock Alert Banner */}
          {stockItems.filter(s => s.low_stock).length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="low-stock-alert">
              <TriangleAlert className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-red-800 text-sm">Low Stock Alert</p>
                <p className="text-red-700 text-xs mt-0.5">
                  {stockItems.filter(s => s.low_stock).length} item{stockItems.filter(s => s.low_stock).length > 1 ? 's' : ''} below minimum threshold:
                  {' '}{stockItems.filter(s => s.low_stock).map(s => s.name).join(', ')}
                </p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stockItems.map(s => (
              <Card key={s.id} className={`${s.low_stock ? 'border-red-400 bg-red-50/40 ring-1 ring-red-200' : s.current_stock <= 0 ? 'border-red-300 bg-red-50/30' : s.current_stock < (s.min_stock_threshold || 0) * 1.5 && s.min_stock_threshold > 0 ? 'border-amber-300 bg-amber-50/30' : ''}`} data-testid={`stock-card-${s.id}`}>
                <CardContent className="py-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{s.name}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant={s.category === "Dye" ? "default" : "secondary"} className="text-xs">{s.category}</Badge>
                        <span className="text-xs text-muted-foreground">Usage: {s.standard_usage_pct}%</span>
                      </div>
                      {s.min_stock_threshold > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">Min: {s.min_stock_threshold.toFixed(2)} {s.unit}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${s.low_stock ? 'text-red-600' : s.current_stock <= 0 ? 'text-red-600' : ''}`}>{s.current_stock.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{s.unit}</p>
                      {s.low_stock && (
                        <Badge className="bg-red-100 text-red-700 text-xs mt-1" data-testid={`low-stock-badge-${s.id}`}>Low Stock</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {stockItems.length === 0 && (
              <Card className="col-span-full"><CardContent className="py-8 text-center text-muted-foreground">No products in inventory</CardContent></Card>
            )}
          </div>
        </TabsContent>

        {/* ─── Reconciliation Tab ─── */}
        {canRecon && (
          <TabsContent value="reconciliation" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Reconciliation</CardTitle>
                <CardDescription>Compare expected vs actual consumption for a date range</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input type="date" value={reconStartDate} onChange={e => setReconStartDate(e.target.value)} className="w-40" data-testid="recon-start" />
                  </div>
                  <div>
                    <Label className="text-xs">End Date</Label>
                    <Input type="date" value={reconEndDate} onChange={e => setReconEndDate(e.target.value)} className="w-40" data-testid="recon-end" />
                  </div>
                  <Button onClick={fetchRecon} disabled={loading} data-testid="recon-generate-btn">
                    {loading ? "Loading..." : "Generate Report"}
                  </Button>
                </div>

                {reconData && (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-blue-100 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-700">{reconData.summary.total_products}</div>
                        <div className="text-xs text-blue-600">Total Products</div>
                      </div>
                      <div className="bg-green-100 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-700">{reconData.summary.within_control}</div>
                        <div className="text-xs text-green-600">Within Control</div>
                      </div>
                      <div className="bg-red-100 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-700">{reconData.summary.over_consumption}</div>
                        <div className="text-xs text-red-600">Over Consumption</div>
                      </div>
                      <div className="bg-amber-100 p-3 rounded-lg text-center">
                        <div className="text-2xl font-bold text-amber-700">{reconData.summary.under_consumption}</div>
                        <div className="text-xs text-amber-600">Under Consumption</div>
                      </div>
                    </div>

                    {/* Reconciliation Table */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead>Product</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead className="text-right">Total Lot Kgs</TableHead>
                            <TableHead className="text-right">Expected</TableHead>
                            <TableHead className="text-right">Actual</TableHead>
                            <TableHead className="text-right">Variance</TableHead>
                            <TableHead className="text-right">Variance %</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reconData.products.map(r => (
                            <TableRow key={r.product_id} className={
                              r.status === 'over_consumption' ? 'bg-red-50/60' :
                              r.status === 'under_consumption' ? 'bg-amber-50/60' : ''
                            }>
                              <TableCell className="font-medium">{r.product_name}</TableCell>
                              <TableCell><Badge variant="secondary" className="text-xs">{r.category}</Badge></TableCell>
                              <TableCell className="text-right font-mono">{r.total_lot_kgs}</TableCell>
                              <TableCell className="text-right font-mono text-blue-700">{r.total_expected}</TableCell>
                              <TableCell className="text-right font-mono font-bold">{r.total_actual}</TableCell>
                              <TableCell className={`text-right font-mono font-bold ${r.variance > 0 ? 'text-red-600' : r.variance < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                {r.variance > 0 ? '+' : ''}{r.variance}
                              </TableCell>
                              <TableCell className={`text-right font-mono font-bold ${Math.abs(r.variance_pct) > settings.tolerance_pct * 2 ? 'text-red-700' : Math.abs(r.variance_pct) > settings.tolerance_pct ? 'text-amber-600' : 'text-green-600'}`}>
                                {r.variance_pct > 0 ? '+' : ''}{r.variance_pct}%
                              </TableCell>
                              <TableCell className="text-center">
                                {r.status === 'within_control' && <Badge className="bg-green-100 text-green-800">Within Control</Badge>}
                                {r.status === 'over_consumption' && <Badge className="bg-red-100 text-red-800">Over Consumption</Badge>}
                                {r.status === 'under_consumption' && <Badge className="bg-amber-100 text-amber-800">Under Consumption</Badge>}
                              </TableCell>
                            </TableRow>
                          ))}
                          {reconData.products.length === 0 && (
                            <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No consumption data in this period</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ─── Product Dialog ─── */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add Chemical / Dye"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Product Name *</Label><Input value={productForm.name} onChange={e => setProductForm({ ...productForm, name: e.target.value })} data-testid="product-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={productForm.category} onValueChange={v => setProductForm({ ...productForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Chemical">Chemical</SelectItem>
                    <SelectItem value="Dye">Dye</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={productForm.unit} onValueChange={v => setProductForm({ ...productForm, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Kg">Kg</SelectItem>
                    <SelectItem value="Gram">Gram</SelectItem>
                    <SelectItem value="Litre">Litre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Standard Usage % *</Label>
              <Input
                type="number" step="0.01" value={productForm.standard_usage_pct}
                onChange={e => setProductForm({ ...productForm, standard_usage_pct: e.target.value })}
                disabled={editingProduct && !isAdmin}
                data-testid="usage-pct-input"
              />
              {editingProduct && !isAdmin && <p className="text-xs text-amber-600 mt-1">Only admin can change Usage % after creation</p>}
              {editingProduct && isAdmin && <p className="text-xs text-blue-600 mt-1">Admin override — changes will be logged</p>}
            </div>
            <div>
              <Label>Min Stock Threshold *</Label>
              <Input
                type="number" step="0.01" value={productForm.min_stock_threshold}
                onChange={e => setProductForm({ ...productForm, min_stock_threshold: e.target.value })}
                disabled={editingProduct && !isAdmin}
                placeholder="Alert when stock falls below this"
                data-testid="min-threshold-input"
              />
              {editingProduct && !isAdmin && <p className="text-xs text-amber-600 mt-1">Only admin can change threshold after creation</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveProduct} disabled={!productForm.name || !productForm.standard_usage_pct} data-testid="save-product-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Inward Dialog ─── */}
      <Dialog open={showInwardDialog} onOpenChange={setShowInwardDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingInward ? "Edit Inward Entry" : "Add Inward Entry"}</DialogTitle><DialogDescription>Record stock received</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product *</Label>
              <div className="relative">
                <Input
                  placeholder="Type to search products..."
                  value={inwardProductSearch}
                  onChange={e => { setInwardProductSearch(e.target.value); }}
                  data-testid="inward-product-select"
                />
                {inwardProductSearch && !inwardForm.product_id && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {products.filter(p => p.name.toLowerCase().includes(inwardProductSearch.toLowerCase()) || p.category.toLowerCase().includes(inwardProductSearch.toLowerCase())).map(p => (
                      <div key={p.id} className="px-3 py-2 hover:bg-muted cursor-pointer text-sm" onClick={() => {
                        setInwardForm({ ...inwardForm, product_id: p.id, entry_unit: p.unit || "" });
                        setInwardProductSearch(`${p.name} (${p.category})`);
                      }}>
                        {p.name} <span className="text-muted-foreground">({p.category})</span>
                      </div>
                    ))}
                    {products.filter(p => p.name.toLowerCase().includes(inwardProductSearch.toLowerCase()) || p.category.toLowerCase().includes(inwardProductSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No products found</div>
                    )}
                  </div>
                )}
                {inwardForm.product_id && (
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => { setInwardForm({ ...inwardForm, product_id: "", entry_unit: "" }); setInwardProductSearch(""); }}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date *</Label><Input type="date" value={inwardForm.date} onChange={e => setInwardForm({ ...inwardForm, date: e.target.value })} /></div>
              <div>
                <Label>Quantity *</Label>
                <div className="flex gap-2">
                  <Input type="number" step="0.01" value={inwardForm.quantity} onChange={e => setInwardForm({ ...inwardForm, quantity: e.target.value })} data-testid="inward-qty-input" className="flex-1" />
                  {inwardForm.product_id && (() => {
                    const prod = products.find(p => p.id === inwardForm.product_id);
                    const opts = getUnitOptions(prod?.unit);
                    return opts.length > 1 ? (
                      <Select value={inwardForm.entry_unit} onValueChange={v => setInwardForm({ ...inwardForm, entry_unit: v })}>
                        <SelectTrigger className="w-24" data-testid="inward-unit-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{opts.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <span className="flex items-center text-sm text-muted-foreground px-2">{prod?.unit}</span>;
                  })()}
                </div>
                {inwardForm.product_id && inwardForm.quantity && inwardForm.entry_unit && (() => {
                  const prod = products.find(p => p.id === inwardForm.product_id);
                  if (inwardForm.entry_unit !== prod?.unit) {
                    const converted = convertToMasterUnit(inwardForm.quantity, inwardForm.entry_unit, prod?.unit);
                    return <p className="text-xs text-blue-600 mt-1">Will be stored as {converted.toFixed(3)} {prod?.unit}</p>;
                  }
                  return null;
                })()}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Supplier</Label><Input value={inwardForm.supplier_name} onChange={e => setInwardForm({ ...inwardForm, supplier_name: e.target.value })} /></div>
              <div><Label>Rate</Label><Input type="number" step="0.01" value={inwardForm.rate} onChange={e => setInwardForm({ ...inwardForm, rate: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowInwardDialog(false); setEditingInward(null); }}>Done</Button>
            <Button onClick={handleSaveInward} disabled={!inwardForm.product_id || !inwardForm.quantity} data-testid="save-inward-btn">{editingInward ? "Update" : "Save & Next"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Outward Dialog ─── */}
      <Dialog open={showOutwardDialog} onOpenChange={setShowOutwardDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingOutward ? "Edit Outward Entry" : "Add Outward Entry (Consumption)"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product *</Label>
              <div className="relative">
                <Input
                  placeholder="Type to search products..."
                  value={outwardProductSearch}
                  onChange={e => { setOutwardProductSearch(e.target.value); }}
                  data-testid="outward-product-select"
                />
                {outwardProductSearch && !outwardForm.product_id && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {products.filter(p => p.name.toLowerCase().includes(outwardProductSearch.toLowerCase()) || p.category.toLowerCase().includes(outwardProductSearch.toLowerCase())).map(p => (
                      <div key={p.id} className="px-3 py-2 hover:bg-muted cursor-pointer text-sm" onClick={() => {
                        setOutwardForm({ ...outwardForm, product_id: p.id, usage_pct: String(p.standard_usage_pct), entry_unit: p.unit || "" });
                        setOutwardProductSearch(`${p.name} (Stock: ${p.current_stock?.toFixed(2)} ${p.unit})`);
                      }}>
                        {p.name} <span className="text-muted-foreground">(Stock: {p.current_stock?.toFixed(2)} {p.unit})</span>
                      </div>
                    ))}
                    {products.filter(p => p.name.toLowerCase().includes(outwardProductSearch.toLowerCase()) || p.category.toLowerCase().includes(outwardProductSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No products found</div>
                    )}
                  </div>
                )}
                {outwardForm.product_id && (
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => { setOutwardForm({ ...outwardForm, product_id: "", usage_pct: "", entry_unit: "" }); setOutwardProductSearch(""); }}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date *</Label><Input type="date" value={outwardForm.date} onChange={e => setOutwardForm({ ...outwardForm, date: e.target.value })} /></div>
              <div><Label>Lot No *</Label><Input value={outwardForm.lot_no} onChange={e => setOutwardForm({ ...outwardForm, lot_no: e.target.value })} data-testid="outward-lot-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lot Kgs (Fabric Weight) *</Label><Input type="number" step="0.01" value={outwardForm.lot_kgs} onChange={e => setOutwardForm({ ...outwardForm, lot_kgs: e.target.value })} data-testid="outward-lotkgs-input" /></div>
              <div>
                <Label>Usage % {!isAdmin && "(from master)"}</Label>
                <Input type="number" step="0.01" value={outwardForm.usage_pct} onChange={e => setOutwardForm({ ...outwardForm, usage_pct: e.target.value })} disabled={!isAdmin} />
              </div>
            </div>
            {expectedConsumption && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                Expected Consumption: <strong>{expectedConsumption} {selectedOutwardProduct?.unit}</strong>
              </div>
            )}
            <div>
              <Label>Qty Used (Manual Entry) *</Label>
              <div className="flex gap-2">
                <Input type="number" step="0.001" value={outwardForm.qty_used} onChange={e => setOutwardForm({ ...outwardForm, qty_used: e.target.value })} data-testid="outward-qtyused-input" className="flex-1" />
                {outwardForm.product_id && (() => {
                  const prod = products.find(p => p.id === outwardForm.product_id);
                  const opts = getUnitOptions(prod?.unit);
                  return opts.length > 1 ? (
                    <Select value={outwardForm.entry_unit} onValueChange={v => setOutwardForm({ ...outwardForm, entry_unit: v })}>
                      <SelectTrigger className="w-24" data-testid="outward-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{opts.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="flex items-center text-sm text-muted-foreground px-2">{prod?.unit}</span>;
                })()}
              </div>
              {selectedOutwardProduct && <p className="text-xs text-muted-foreground mt-1">Available stock: {selectedOutwardProduct.current_stock?.toFixed(2)} {selectedOutwardProduct.unit}</p>}
              {outwardForm.product_id && outwardForm.qty_used && outwardForm.entry_unit && (() => {
                const prod = products.find(p => p.id === outwardForm.product_id);
                if (outwardForm.entry_unit !== prod?.unit) {
                  const converted = convertToMasterUnit(outwardForm.qty_used, outwardForm.entry_unit, prod?.unit);
                  return <p className="text-xs text-blue-600 mt-1">Will be stored as {converted.toFixed(3)} {prod?.unit}</p>;
                }
                return null;
              })()}
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowOutwardDialog(false); setEditingOutward(null); }}>Done</Button>
            <Button onClick={handleSaveOutward} disabled={!outwardForm.product_id || !outwardForm.lot_no || !outwardForm.lot_kgs || !outwardForm.qty_used} data-testid="save-outward-btn">{editingOutward ? "Update" : "Save & Next"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Tolerance Dialog ─── */}
      <Dialog open={showToleranceDialog} onOpenChange={setShowToleranceDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Set Tolerance Range</DialogTitle><DialogDescription>Global tolerance for variance alerts</DialogDescription></DialogHeader>
          <div>
            <Label>Tolerance % (±)</Label>
            <Input type="number" step="0.5" min="0" max="20" value={toleranceForm} onChange={e => setToleranceForm(e.target.value)} data-testid="tolerance-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowToleranceDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTolerance} data-testid="save-tolerance-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
